import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, serverTimestamp, getDoc, where, setDoc, arrayUnion, addDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';

const PendingOrganizations = ({ onStatusChange }) => {
  const [pendingOrgs, setPendingOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPendingOrganizations();
  }, []);

  const fetchPendingOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try querying with status filter first
      let querySnapshot;
      try {
        const q = query(
          collection(db, 'organizations'),
          where('status', '==', 'pending')
        );
        querySnapshot = await getDocs(q);
      } catch (queryError) {
        // If query fails (e.g., missing index), fall back to fetching all and filtering
        console.warn('Status query failed, fetching all organizations:', queryError);
        const allOrgsSnap = await getDocs(collection(db, 'organizations'));
        const filteredOrgs = allOrgsSnap.docs
          .filter(doc => {
            const data = doc.data();
            return data.status === 'pending' || (!data.status && data.createdAt); // Include orgs without status if they have createdAt
          })
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        setPendingOrgs(filteredOrgs);
        setLoading(false);
        return;
      }
      
      const orgs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPendingOrgs(orgs);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pending organizations:', err);
      setError(`Failed to load pending organizations: ${err.message}`);
      setLoading(false);
    }
  };

  const handleApprove = async (org) => {
    try {
      const now = serverTimestamp();
      
      toast.loading('Approving organization...', { id: 'approveOrg' });
      
      setPendingOrgs(prevOrgs => prevOrgs.filter(o => o.id !== org.id));
      
      // Update organization status
      await updateDoc(doc(db, 'organizations', org.id), {
        status: 'active',
        approvedAt: now,
        approvedBy: auth.currentUser.email,
        updatedAt: now
      });

      // Add the organization to the creator's activeOrgs array
      const creatorUserRef = doc(db, 'users', org.createdBy.uid);
      await updateDoc(creatorUserRef, {
        activeOrgs: arrayUnion(org.id)
      });

      // Update creator's organization in their subcollection
      const userOrgRef = doc(db, 'users', org.createdBy.uid, 'organizations', org.id);
      await updateDoc(userOrgRef, {
        status: 'active',
        role: 'Admin',
        lastActivityAt: now
      });

      // Update user's status in organization's users subcollection
      const orgUserRef = doc(db, 'organizations', org.id, 'users', org.createdBy.uid);
      await updateDoc(orgUserRef, {
        status: 'active',
        lastActivityAt: now
      });

      // Create email to be sent to the creator
      await addDoc(collection(db, "mail"), {
        to: org.createdBy.email,
        template: {
          name: "organizationApproved",
          data: {
            orgName: org.name,
            creatorName: org.createdBy.name || 'Friend',
            orgLink: `${window.location.origin}/organization/${org.id}`
          },
        },
      });

      // Create notification in user's notifications subcollection
      const notificationRef = doc(collection(db, 'users', org.createdBy.uid, 'notifications'));
      await setDoc(notificationRef, {
        type: 'success',
        title: 'Organization Approved',
        message: `Your organization "${org.name}" has been approved!`,
        createdAt: now,
        read: false,
        orgId: org.id,
        action: {
          text: 'View Organization',
          link: `/organization/${org.id}`
        }
      });

      toast.dismiss('approveOrg');
      toast.success(`${org.name} has been approved!`);
      onStatusChange?.();

    } catch (err) {
      console.error('Error approving organization:', err);
      toast.dismiss('approveOrg');
      toast.error(`Failed to approve ${org.name}`);
      await fetchPendingOrganizations();
    }
  };

  const handleReject = async (org) => {
    try {
      const now = serverTimestamp();
      
      toast.loading('Rejecting organization...', { id: 'rejectOrg' });
      
      // Update organization status
      await updateDoc(doc(db, 'organizations', org.id), {
        status: 'rejected',
        rejectedAt: now,
        rejectedBy: auth.currentUser.email,
        updatedAt: now
      });

      // Update creator's organization in their subcollection
      const userOrgRef = doc(db, 'users', org.createdBy.uid, 'organizations', org.id);
      await updateDoc(userOrgRef, {
        status: 'rejected',
        lastActivityAt: now
      });

      // Update user's status in organization's users subcollection
      const orgUserRef = doc(db, 'organizations', org.id, 'users', org.createdBy.uid);
      await updateDoc(orgUserRef, {
        status: 'rejected',
        lastActivityAt: now
      });

      // Create rejection notification
      const notificationRef = doc(collection(db, 'users', org.createdBy.uid, 'notifications'));
      await setDoc(notificationRef, {
        type: 'error',
        title: 'Organization Application Rejected',
        message: `Your application for "${org.name}" has been rejected. Please contact support for more information.`,
        createdAt: now,
        read: false,
        orgId: org.id
      });

      toast.dismiss('rejectOrg');
      toast.success(`${org.name} has been rejected`);
      setPendingOrgs(prevOrgs => prevOrgs.filter(o => o.id !== org.id));
      onStatusChange?.();

    } catch (err) {
      console.error('Error rejecting organization:', err);
      toast.dismiss('rejectOrg');
      toast.error(`Failed to reject ${org.name}`);
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;

  return (
    <div className="p-4">
      {pendingOrgs.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No pending organizations to review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingOrgs.map((org) => (
            <div key={org.id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{org.name}</h3>
                  <p className="text-sm text-gray-600">Display Name: {org.displayName}</p>
                  
                  <div className="mt-2 text-sm text-gray-700">
                    <p className="font-medium">Bio:</p>
                    <p className="whitespace-pre-wrap">{org.bio}</p>
                  </div>

                  <div className="mt-2 space-y-1 text-sm">
                    <p className="font-medium">Location:</p>
                    <div className="ml-2">
                      <p>Country: {org.vitals?.country}</p>
                      <p>Zip Code: {org.vitals?.zipCode}</p>
                      {org.vitals?.website && (
                        <p>
                          Website:{' '}
                          <a 
                            href={org.vitals.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {org.vitals.website}
                          </a>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 space-y-1 text-sm">
                    <p>Created by: {org.createdBy.email}</p>
                    <p>Submitted: {org.createdAt?.toDate()?.toLocaleDateString() || 'N/A'}</p>
                    <p>Members: {org.memberCount}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleApprove(org)}
                    className="flex items-center px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(org)}
                    className="flex items-center px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                  >
                    <XCircleIcon className="h-5 w-5 mr-1" />
                    Reject
                  </button>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="font-medium mb-2">Settings</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Allow Self Join: {org.settings.allowSelfJoin ? 'Yes' : 'No'}</div>
                  <div>Default Role: {org.settings.defaultRole}</div>
                  <div className="flex items-center">
                    Brand Color: 
                    <div 
                      className="w-4 h-4 ml-2 rounded-full" 
                      style={{ backgroundColor: org.settings.primaryColor }}
                    />
                  </div>
                  <div>
                    Auto-approve Stories: {org.settings.storySettings?.autoApprove ? 'Yes' : 'No'}
                  </div>
                  <div>
                    Allow Member Stories: {org.settings.storySettings?.allowMemberStories ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingOrganizations; 